/**
 * The four degraded states (loading, unavailable, empty, not-found). Each
 * renders inside the Tool only — the Webflow page around it stays intact.
 */
import { h, heading } from "../dom";

export type StateKind = "loading" | "unavailable" | "empty" | "not-found";
export type StateSubject = "directory" | "election" | "person" | "comparison";

interface StateOptions {
  subject: StateSubject;
  /** 1 when the state stands in for the whole view, so the view keeps its one <h1>. */
  headingLevel?: number;
  /** Unavailable only: called after the Retry button is pressed. */
  onRetry?: () => void;
  /** Not-found only: where "all elections" lives. */
  directoryHref?: string;
}

const COPY: Record<StateKind, Record<StateSubject, [string, string]>> = {
  loading: {
    directory: ["Loading elections", "Finding every published election."],
    election: ["Loading election", "Finding this election's Races and candidates."],
    person: ["Loading profile", "Finding this person's public record."],
    comparison: ["Loading questionnaires", "Finding every candidate's answers."],
  },
  unavailable: {
    directory: ["Elections are temporarily unavailable.", "Try again shortly."],
    election: [
      "Election information is temporarily unavailable.",
      "Try again shortly. No stale candidate or questionnaire status is being shown.",
    ],
    person: ["This profile is temporarily unavailable.", "Try again shortly."],
    comparison: ["Questionnaires are temporarily unavailable.", "Try again shortly."],
  },
  empty: {
    directory: ["No elections are published yet.", "Election pages appear here once they are published."],
    election: ["No public Races are available yet.", "Races and candidates appear here once they are published."],
    person: ["Nothing is published about this person yet.", ""],
    comparison: ["No questionnaire covers this Race.", ""],
  },
  "not-found": {
    directory: ["Not found", ""],
    election: ["We couldn't find that election.", "It may have been renamed or not yet published."],
    person: ["We couldn't find that person.", "Their page may not be published."],
    comparison: ["No questionnaire covers this Race.", ""],
  },
};

export function renderState(kind: StateKind, container: HTMLElement, opts: StateOptions): HTMLElement {
  const [title, body] = COPY[kind][opts.subject];
  const block = h(
    "div",
    {
      class: `bsc-state bsc-state--${kind}`,
      "data-state": kind,
      role: kind === "loading" ? "status" : null,
      "aria-busy": kind === "loading" ? "true" : null,
    },
    heading(opts.headingLevel ?? 2, { class: "bsc-state__title" }, title),
    body ? h("p", { class: "bsc-state__body" }, body) : null,
    kind === "unavailable" && opts.onRetry
      ? h("button", { type: "button", class: "bsc-state__retry", onClick: opts.onRetry }, "Try again")
      : null,
    kind === "not-found" && opts.directoryHref
      ? h("p", null, h("a", { class: "bsc-state__link", href: opts.directoryHref }, "See all elections"))
      : null
  );
  container.replaceChildren(block);
  return block;
}
